import {HeartIcon, HeartIconFilled} from '@/assets/icons/icons'
import {Spinner} from '@/components/ui/spinner/Spinner'
import dayjs, {UnitsE} from '@/dayjs'
import {useMe} from '@/features/authentication/queries/useMe'
import {GuestTypeV3, HostTypeV3} from '@/features/authentication/types'
import {useGetSavedHosts} from '@/features/guest/queries/useGetSavedHosts'
import {useSaveHost} from '@/features/guest/queries/useSaveHost'
import {useUnsaveHost} from '@/features/guest/queries/useUnsaveHost'
import {useHandleError} from '@/hooks/useHandleError'
import {useRootStore} from '@/store'
import {selectCancelRecurringGiftModal} from '@/store/selectors'
import {USER_ROLE_GUEST} from '@/utilities/constants/user'
import {FC, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import BlockHostButton from '../block-host-button/BlockHostButton'
import {ConnectNowButton} from '../cta-bar/CtaBar'
import {StyledActionBtn, StyledActionBtnWrapper} from '../host-profile-cta-section/style'
import HostProfileGoalSection from '../host-profile-goal-section/HostProfileGoalSection'
import HostProfileMuralSection from '../host-profile-mural-section/HostProfileMuralSection'
import {MemberShipPlans} from '../membersip-plans/MemberShipPlans'
import {
    ContainerRow,
    StyledAvatar,
    StyledAvatarWrapper,
    StyledConnectContainer,
    StyledContainer,
    StyledFullName,
    StyledGoBackButtonIcon,
    StyledHeartButton,
    StyledHeartSection,
    StyledHostProfileWrapper,
    StyledInformSection,
    StyledInformSectionRow,
    StyledInformSectionRowContent,
    StyledInformSectionRowTitle,
    StyledShareButton,
    StyledStickContainer
} from './style'
import {ShareProfileModal} from '@/features/host/components/share-profile-modal/ShareProfileModal'
import {GenderKeyNameE} from '@/features/constants/constants'
import {Translate} from '@/features/translation/components/translate/Translate'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {useGetCreatorProfile} from '@/features/guest/queries/useGetCreatorProfile'
import {useGetSubscribedHostsV3} from '@/features/recurring-gift/queries/useGetSubscribedHostsV3'

type HostProfileProps = {
    hostUserName?: string
    isModal?: boolean
    closeModal?: () => void
}

export const HostProfile: FC<HostProfileProps> = ({hostUserName, isModal = false, closeModal}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {openModal: openCancelRecurringGiftModal} = useRootStore(selectCancelRecurringGiftModal)
    // user
    const {data: subscribedHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })

    const {data: user, isFetching: isLoadingMe} = useMe()
    const guestProfile = user as GuestTypeV3
    const isGuest = user?.type === USER_ROLE_GUEST
    const {
        data: hostByUsername,
        isFetching: isLoadingHost,
        refetch: refetchHost,
        isError,
        error
    } = useGetCreatorProfile({creatorIdentifier: hostUserName ?? null})

    const isSubscribedHost = subscribedHosts?.find(subscribePack => subscribePack.rookieId === hostByUsername?.id)
    const {mutate: saveHost, isPending: isPendingSave} = useSaveHost(refetchHost)
    const {mutate: unsaveHost, isPending: isPendingUnsave} = useUnsaveHost(refetchHost)
    const isLoadingLike = isPendingUnsave || isPendingSave

    const likesCounter = hostByUsername?.typeAttributes?.followersCount
        ? hostByUsername.typeAttributes.followersCount
        : 0
    const {data: savedHosts, isLoading: isLoadingSavedHosts} = useGetSavedHosts(isGuest && !!hostByUsername)
    const isLiked = !!savedHosts?.find(host => host.id === hostByUsername?.id)

    const onClickLikeButton = () => {
        if (isLoadingLike || !hostByUsername?.id) return
        if (isLiked) unsaveHost(hostByUsername.id)
        else saveHost(hostByUsername.id)
    }
    const sizeOfHeart = 20
    const hostData = isGuest ? (hostByUsername as HostTypeV3) : (user as HostTypeV3)
    const isLoading = isLoadingMe || isLoadingHost || isLoadingSavedHosts
    const hasPastGoals = hostData?.typeAttributes?.hasPastGoals
    const hostsWish = hostData?.typeAttributes?.path

    const onGoBackHandler = () => {
        if (isModal) closeModal?.()
        else navigate(-1)
    }
    useHandleError({isError, error, isBlankPage: true})

    // mixpanel

    const activeFilters = guestProfile.typeAttributes?.followerCarouselFilters //TODO: ADD FILTERS
    const countryFilters = activeFilters
        ? guestProfile.typeAttributes?.followerCarouselFilters?.filters.countries.map(country => country.name)
        : []
    const [endTime, setEndTime] = useState<dayjs.Dayjs>(dayjs())
    const handleSetEndTime = () => {
        const endTime = dayjs()
        setEndTime(endTime)
    }
    const startTime = dayjs()

    const mixpanelData = {
        carousel_card_number: 0,
        click_share: false,
        country_filter_applied: countryFilters?.length ? countryFilters : 'No',
        from: 'Profile',
        morgi_balance: guestProfile.typeAttributes?.microMorgiBalance,
        scrolled_images: 0,
        time_on_profile: startTime.diff(endTime, UnitsE.MILLISECOND),
        watched_video: false
    }
    const hostGender = useMemo(() => {
        switch (hostData?.genderId) {
            case 1:
                return GenderKeyNameE.MALE
            case 2:
                return GenderKeyNameE.FEMALE
            default:
                return GenderKeyNameE.OTHER
        }
    }, [hostData])

    const [isOpenShareProfileModal, setIsOpenShareProfileModal] = useState(false)

    const onShareClick = () => {
        // setIsClickShare(true)
        setIsOpenShareProfileModal(true)
    }

    return (
        <>
            <StyledHostProfileWrapper>
                {(isLoading || isLoadingLike) && <Spinner />}

                <StyledAvatarWrapper>
                    <StyledAvatar src={hostData?.avatar?.url}></StyledAvatar>
                    <StyledGoBackButtonIcon onClick={onGoBackHandler} />
                    <StyledShareButton onClick={onShareClick} />
                </StyledAvatarWrapper>
                <StyledContainer>
                    <StyledStickContainer>
                        <StyledConnectContainer>
                            <StyledFullName>{hostData?.fullName}</StyledFullName>
                            {hostData && isGuest && (
                                <ConnectNowButton
                                    hostId={hostData.id}
                                    mixpanelData={mixpanelData}
                                    className={'connectNowButton'}
                                    handleSetEndTime={handleSetEndTime}
                                />
                            )}
                        </StyledConnectContainer>
                    </StyledStickContainer>

                    <StyledHeartSection onClick={isGuest ? onClickLikeButton : undefined}>
                        <StyledHeartButton>
                            {isLiked || !isGuest ? (
                                <HeartIconFilled width={sizeOfHeart} height={sizeOfHeart} />
                            ) : (
                                <HeartIcon width={sizeOfHeart} height={sizeOfHeart} />
                            )}
                        </StyledHeartButton>

                        {likesCounter >= 100 && (
                            <span>{likesCounter > 1000 ? (likesCounter / 1000).toFixed(3) : likesCounter}</span>
                        )}
                    </StyledHeartSection>
                    <StyledInformSection>
                        {hostsWish?.name || hostData?.description ? (
                            <StyledInformSectionRow>
                                {hostsWish?.name ? (
                                    <StyledInformSectionRowTitle>
                                        {t('rookie_profile_page:host_is', {PROFESSION: hostsWish.name})}
                                    </StyledInformSectionRowTitle>
                                ) : null}
                                {hostData?.description ? (
                                    <StyledInformSectionRowContent>
                                        <Translate isUseMainAppLanguage={true} content={hostData.description} />
                                    </StyledInformSectionRowContent>
                                ) : null}
                            </StyledInformSectionRow>
                        ) : null}
                    </StyledInformSection>

                    {hostData?.id && (
                        <>
                            {isGuest && (
                                <ContainerRow>
                                    <MemberShipPlans hostId={hostData.id} />
                                </ContainerRow>
                            )}

                            <ContainerRow>
                                <HostProfileGoalSection hostId={hostData.id} hasPastGoals={hasPastGoals} />
                            </ContainerRow>
                            <HostProfileMuralSection host={hostData} />
                            {isGuest && (
                                <ContainerRow>
                                    <StyledActionBtnWrapper>
                                        <BlockHostButton hostName={hostData.fullName as string} hostId={hostData.id} />
                                        {!!isSubscribedHost && (
                                            <StyledActionBtn
                                                onClick={() =>
                                                    openCancelRecurringGiftModal({
                                                        subscriptionId: `${isSubscribedHost.id}`,
                                                        hostID: `${isSubscribedHost.rookieId}`
                                                    })
                                                }
                                            >
                                                {t('common:cancel_gift')}
                                            </StyledActionBtn>
                                        )}
                                    </StyledActionBtnWrapper>
                                </ContainerRow>
                            )}
                        </>
                    )}
                </StyledContainer>
            </StyledHostProfileWrapper>
            {isOpenShareProfileModal && (
                <ShareProfileModal
                    hostGender={hostGender}
                    username={hostData?.username}
                    onClose={() => setIsOpenShareProfileModal(false)}
                />
            )}
        </>
    )
}

HostProfile.displayName = 'HostProfile'
